import { Box } from '@mui/material'
import type { SwipeableDrawerProps } from '@mui/material/SwipeableDrawer'
import MuiSwipeableDrawer from '@mui/material/SwipeableDrawer'
import { styled } from '@mui/material/styles'
import type { PropsWithChildren } from 'react'

import { useSettings } from '@core/context/settingsContext'

import { useBannerOffset } from '~/store/slices/display'

type Props = PropsWithChildren<{
  navWidth: number
  navVisible: boolean
  collapsedNavWidth: number
  hidden: boolean
  navigationBorderWidth: number
  setNavVisible: (value: boolean) => void
}>

const SwipeableDrawer = styled(MuiSwipeableDrawer)<SwipeableDrawerProps>({
  // `overflow: visible` lets the collapsed-state expand chevron overlay the
  // drawer's right border without being clipped. We can't just clear
  // `overflow-x` here because the paper's default `overflow-y: auto` would
  // force `overflow-x` to compute back to `auto`. The inner nav container
  // (`<Box component="nav">`) handles its own y-scroll, so the paper doesn't
  // need scrolling itself.
  overflow: 'visible',
  transition: 'width .25s ease-in-out',
  '& ul': {
    listStyle: 'none'
  },
  '& .MuiListItem-gutters': {
    paddingLeft: 4,
    paddingRight: 4
  },
  '& .MuiDrawer-paper': {
    left: 'unset',
    right: 'unset',
    overflow: 'visible',
    transition: 'width .25s ease-in-out, box-shadow .25s ease-in-out'
  }
})

const Drawer = (props: Props) => {
  const bannerOffset = useBannerOffset()

  const { hidden, children, navWidth, navVisible, setNavVisible, collapsedNavWidth, navigationBorderWidth } = props

  const { navCollapsed } = useSettings().settings

  // Drawer Props for Mobile & Tablet screens
  const MobileDrawerProps = {
    open: navVisible,
    onOpen: () => setNavVisible(true),
    onClose: () => setNavVisible(false),
    ModalProps: {
      keepMounted: true // Better open performance on mobile.
    }
  }

  // Drawer Props for Laptop & Desktop screens
  const DesktopDrawerProps = {
    open: true,
    onOpen: () => null,
    onClose: () => null
  }

  return (
    <SwipeableDrawer
      className="layout-vertical-nav"
      variant={hidden ? 'temporary' : 'permanent'}
      {...(hidden ? { ...MobileDrawerProps } : { ...DesktopDrawerProps })}
      PaperProps={{
        sx: {
          width: navCollapsed ? collapsedNavWidth : navWidth,
          // Dark mode frames the near-black canvas with a lighter gray rail
          // (the navCard tone); light mode keeps the shared layout background.
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? theme.palette.background.navCard : theme.palette.background.layout,
          borderRight: (theme) =>
            navigationBorderWidth === 0 ? 0 : `${navigationBorderWidth}px solid ${theme.palette.divider}`,
          // Make the paper a flex column so the banner-offset spacer and the
          // navigation body share the available height. Without this, the
          // body's `height: 100%` resolves against the full paper height
          // (≈100vh) and the banner spacer pushes the bottom of the nav body
          // off the viewport whenever a banner is active.
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      sx={{
        width: navCollapsed ? collapsedNavWidth : navWidth
      }}
    >
      {/* Consumes the same vertical space as the banner, offsetting the remaining drawer content */}
      <Box height={bannerOffset} flexShrink={0} />
      {children}
    </SwipeableDrawer>
  )
}

export default Drawer
